/**
 * CertFormsPanel — 官庁様式(雇入/変更/航行報告/航行報告証明申請)と社内様式
 * (船用品注文書/船員旅費精算書)の入力＋出力パネル。
 * 証明書発行ページ(CertificatesPage)から form 種別を渡して表示する。
 *  - 入力 → バックエンド(/cert-forms/:form)で 実テンプレ流し込み(雇入)/忠実HTML(航行報告) → PDF/Word/Excel
 *  - 右にPNGプレビュー(ズーム・スクロール・倍率保存)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Download, Printer } from 'lucide-react';
import * as api from '../services/api';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
function subdomain(): string {
  const h = window.location.hostname;
  if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
  return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
}
function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': subdomain(), 'X-App-Type': 'admin', 'Content-Type': 'application/json' };
}

export const CERT_FORM_IDS = ['koyoiri', 'henko', 'koko', 'koko-cert', 'shipsupply', 'travelexp', 'koten', 'tenmatsu', 'roumu'] as const;
export type CertForm = typeof CERT_FORM_IDS[number];
const TITLES: Record<CertForm, string> = {
  koyoiri: '雇入（雇止）届出書', henko: '雇入契約変更（更新）届出書', koko: '航行報告書', 'koko-cert': '航行報告証明申請書',
  shipsupply: '船用品注文書', travelexp: '船員旅費精算書', koten: '荒天避難報告書', tenmatsu: '顛末書', roumu: '労務管理記録簿',
};
const isHire = (f: CertForm) => f === 'koyoiri' || f === 'henko';
/** 社内様式（紙の原本を再現したもの）。見本がExcel帳票なのでPDF/Excelで出力する */
const isInternal = (f: CertForm) => f === 'shipsupply' || f === 'travelexp';
/** PDFだけで出す社内様式（荒天避難報告書・顛末書。船上のスマホで書いて提出する前提） */
const isPdfOnly = (f: CertForm) => f === 'koten' || f === 'tenmatsu';
/** 勤怠から作る様式（入力ではなく対象者・期間を選ぶ） */
const isFromAttendance = (f: CertForm) => f === 'roumu';

interface Row {
  bookNo: string; name: string; age: string; distinction: string;
  hireDate: string; hirePort: string; endDate: string; endPort: string;
  position: string; period: string; wage: string; allowance: string; otherConditions: string; remarks: string;
  changeDate: string; changeItem: string; newValue: string; oldValue: string; updatedContract: string;
}
const emptyRow = (): Row => ({ bookNo: '', name: '', age: '', distinction: '', hireDate: '', hirePort: '', endDate: '', endPort: '', position: '', period: '', wage: '', allowance: '', otherConditions: '', remarks: '', changeDate: '', changeItem: '', newValue: '', oldValue: '', updatedContract: '' });

/** 入力候補（陸上のマスタ由来）。extra は品名に紐づく既定の寸法 */
interface OptionItem { value: string; extra?: string }
type OptionMap = Record<string, OptionItem[]>;
const EMPTY_OPTIONS: OptionMap = { supplierName: [], itemName: [], size: [], remarks: [], loadPort: [], station: [], transport: [], jobTitle: [] };

interface SupplyRow { no: string; itemName: string; size: string; stock: string; qty: string; remarks: string; received?: string; }
const emptySupply = (): SupplyRow => ({ no: '', itemName: '', size: '', stock: '', qty: '', remarks: '' });
/** 荒天避難報告書の停泊当直 */
interface WatchRow { from: string; to: string; deck: string; engine: string; }
const emptyWatch = (): WatchRow => ({ from: '', to: '', deck: '', engine: '' });
interface TravelRow { month: string; day: string; fromStation: string; toStation: string; transport: string; amount: string; }
const emptyTravel = (): TravelRow => ({ month: '', day: '', fromStation: '', toStation: '', transport: '', amount: '' });
/** 「1,200」「1200円」などの入力から数値を取り出す */
const yenNum = (v: string): number => { const n = Number(String(v ?? '').replace(/[^\d.-]/g, '')); return isFinite(n) ? n : 0; };

interface Vessel { id: string; vessel_name: string; vessel_type?: string; vessel_no?: string; owner_type?: string; owner_name?: string; owner_address?: string; gross_tonnage?: string | null; port_of_registry?: string | null; main_engine_type?: string | null; main_engine_count?: string | null; main_engine_power?: string | null; }
interface Crew { id: string; user_name: string; employee_code: string; birth_date?: string | null; seaman_book_no?: string | null; license_grade?: string | null; vessel_name?: string | null; }

function ageFromBirth(bd?: string | null): string {
  if (!bd) return ''; const d = new Date(bd); if (isNaN(d.getTime())) return '';
  const now = new Date(); let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a > 0 && a < 120 ? String(a) : '';
}

const CrewPicker: React.FC<{
  crew: Crew[]; posMap: Record<string, string>; onPick: (c: Crew) => void; placeholder?: string;
  /** 選択中の船名。その船の乗船中→過去乗船者を候補の先頭に出す */
  priorityVesselName?: string | null;
  /** 選択中の船に過去（または予定で）乗ったことのある user_id */
  relatedIds?: Set<string>;
}> = ({ crew, posMap, onPick, placeholder, priorityVesselName, relatedIds }) => {
  const [q, setQ] = useState(''); const [open, setOpen] = useState(false);
  const ql = q.trim().toLowerCase();
  const filtered = crew.filter(c => !ql || c.user_name.toLowerCase().includes(ql) || (c.employee_code ?? '').toLowerCase().includes(ql));
  // 選択中の船の関係者（乗船中→過去乗船）を先頭グループにし、残りは従来どおり船ごと
  const prName = priorityVesselName || null;
  const related = relatedIds ?? new Set<string>();
  const tierOn: Crew[] = []; const tierPast: Crew[] = []; const rest: Crew[] = [];
  for (const c of filtered) {
    if (prName && (c.vessel_name ?? '') === prName) tierOn.push(c);
    else if (prName && related.has(c.id)) tierPast.push(c);
    else rest.push(c);
  }
  const groups = new Map<string, Crew[]>();
  if (tierOn.length) groups.set(`${prName}｜乗船中`, tierOn);
  if (tierPast.length) groups.set(`${prName}｜過去に乗船`, tierPast);
  for (const c of rest) { const k = c.vessel_name ?? '未配属'; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(c); }
  const fixedHeads = [...groups.keys()].filter(k => k.includes('｜'));
  const gks = [
    ...fixedHeads,
    ...[...groups.keys()].filter(k => !k.includes('｜'))
      .sort((a, b) => a === '未配属' ? 1 : b === '未配属' ? -1 : a.localeCompare(b, 'ja')),
  ];
  return (
    <div style={{ position: 'relative', marginBottom: 6 }}>
      <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder={placeholder ?? '🔍 船員を選択／検索して自動入力…'}
        style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #bfdbfe', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', background: '#f0f9ff' }} />
      {open && (
        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 300, overflowY: 'auto' }}>
          {gks.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#94a3b8' }}>該当なし</div>}
          {gks.map(gk => (
            <div key={gk}>
              <div style={{ padding: '5px 10px', fontSize: 11, fontWeight: 800, color: '#0369a1', background: '#f0f9ff', borderBottom: '1px solid #e0f2fe', position: 'sticky', top: 0 }}>⛴ {gk}（{groups.get(gk)!.length}名）</div>
              {groups.get(gk)!.map(c => (
                <div key={c.id} onMouseDown={() => { onPick(c); setQ(''); setOpen(false); }}
                  style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                  <span style={{ fontWeight: 700 }}>{c.user_name}</span><span style={{ color: '#94a3b8' }}>{c.employee_code}</span>
                  {posMap[c.id] && <span style={{ color: '#0369a1', marginLeft: 'auto', background: '#e0f2fe', borderRadius: 10, padding: '1px 8px' }}>{posMap[c.id]}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inp: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

/**
 * 候補から選べる入力欄。マスタに無いものはそのまま打ち込める。
 * 候補はマスタ管理の「証明書の入力候補」で登録する。
 */
const Combo: React.FC<{
  value: string; onChange: (v: string) => void; options?: OptionItem[];
  ph?: string; onPickExtra?: (extra: string) => void;
}> = ({ value, onChange, options = [], ph, onPickExtra }) => {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  // 入力済みの文字で絞り込む。完全一致しているときは全件出す（選び直せるように）
  const list = !q || options.some(o => o.value === value) ? options : options.filter(o => o.value.toLowerCase().includes(q));
  return (
    <div style={{ position: 'relative' }}>
      <input style={{ ...inp, paddingRight: options.length > 0 ? 26 : 8 }} value={value} placeholder={ph}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)} />
      {options.length > 0 && (
        <button type="button" tabIndex={-1} aria-label="候補から選ぶ"
          onMouseDown={e => { e.preventDefault(); setOpen(o => !o); }}
          style={{ position: 'absolute', right: 1, top: 1, bottom: 1, width: 22, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 10, cursor: 'pointer' }}>▼</button>
      )}
      {open && list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 220, overflowY: 'auto' }}>
          {list.map(o => (
            <div key={o.value} onMouseDown={() => { onChange(o.value); if (o.extra && onPickExtra) onPickExtra(o.extra); setOpen(false); }}
              style={{ padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', gap: 8 }}
              onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
              <span style={{ flex: 1, minWidth: 0 }}>{o.value}</span>
              {o.extra && <span style={{ color: '#94a3b8', flexShrink: 0 }}>{o.extra}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const lb: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 3, marginTop: 8 };
const ta: React.CSSProperties = { ...inp, resize: 'vertical' };

const CertFormsPanel: React.FC<{ form: CertForm }> = ({ form }) => {
  const [data, setData] = useState<Record<string, string>>({ shipUse: '貨物船', navArea: '沿海区域', mainEngineType: '内燃', bureauName: '四国運輸局長', department: '甲板部' });
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [supplyRows, setSupplyRows] = useState<SupplyRow[]>([emptySupply()]);
  const [travelRows, setTravelRows] = useState<TravelRow[]>([emptyTravel()]);
  const [opts, setOpts] = useState<OptionMap>(EMPTY_OPTIONS);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [crew, setCrew] = useState<Crew[]>([]);
  const [posMap, setPosMap] = useState<Record<string, string>>({});
  /** 選択中の船（船員候補の優先表示に使う） */
  const [selVessel, setSelVessel] = useState<{ id: string; name: string } | null>(null);
  /** 船ごとの関係者（乗船中・過去乗船・予定の user_id）。assignments/list から作る */
  const [vesselUsers, setVesselUsers] = useState<Record<string, string[]>>({});
  const [previewUrl, setPreviewUrl] = useState('');
  const [zoom, setZoom] = useState(() => { const s = parseFloat(localStorage.getItem('cert_preview_zoom') ?? ''); return isFinite(s) && s > 0 ? s : 1.3; });
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string) => setData(d => ({ ...d, [k]: v }));

  useEffect(() => {
    api.get<any>('/users/me/profile').then(r => {
      const c = r?.data ?? r;
      setData(d => ({
        ...d,
        ownerName: d.ownerName || c?.company_name || '',
        ownerAddress: d.ownerAddress || [c?.company_address, c?.company_name].filter(Boolean).join('\n'),
        ownerAddrName: d.ownerAddrName || [c?.company_address, c?.company_name].filter(Boolean).join('\n'),
        // 社内様式の宛先・発行元（船用品注文書の「○○ 御中」、顛末書の「代表取締役 ○○ 様」）
        companyName: d.companyName || c?.company_name || '',
        companyRep: d.companyRep || c?.company_rep || '',
      }));
    }).catch(() => {});
    api.get<any>('/vessels').then(r => setVessels(r?.data ?? [])).catch(() => {});
    api.get<any>('/users', { limit: '500' }).then(r => setCrew((r?.data ?? []).filter((u: any) => ['crew', 'captain'].includes(u.user_role)))).catch(() => {});
    api.get<any>('/vessels/assignments/list').then(r => {
      // 職務の自動入力は「現在の配乗」を使う: on_board を最優先、無ければ最新（応答はboarded_at降順）。
      // 以前は全行を順に上書きしていて最も古い配乗の役職が残り、「船長/消火作業指揮者」のような
      // 現在の併記役職が書類の自動入力に反映されなかった。
      const m: Record<string, string> = {};
      const onBoard = new Set<string>();
      const vu: Record<string, string[]> = {};
      for (const a of (r?.data ?? [])) {
        // 船ごとの関係者（乗船中・過去乗船・予定）。船を選んだとき候補の先頭に出すために使う
        if (a.vessel_id && a.user_id) {
          const list = (vu[a.vessel_id] ??= []);
          if (!list.includes(a.user_id)) list.push(a.user_id);
        }
        if (!a.position) continue;
        if (a.assignment_status === 'on_board' && !onBoard.has(a.user_id)) { m[a.user_id] = a.position; onBoard.add(a.user_id); }
        else if (!(a.user_id in m)) m[a.user_id] = a.position;
      }
      setPosMap(m);
      setVesselUsers(vu);
    }).catch(() => {});
    // 入力候補（マスタ管理の「証明書の入力候補」＋寄港地・役職マスタ）
    api.get<any>('/cert-forms/options').then(r => setOpts({ ...EMPTY_OPTIONS, ...(r?.data ?? {}) })).catch(() => {});
    // 労務管理記録簿の協定・記名者は既存の設定マスタから初期値を入れる
    api.get<any>('/labor-records/settings').then(r => {
      const st = r?.data ?? r ?? {};
      const now = new Date();
      setData(d => ({
        ...d,
        year: d.year || String(now.getFullYear()),
        month: d.month || String(now.getMonth() + 1),
        holidayDefined: d.holidayDefined || '有',
        overtimeAgreement: d.overtimeAgreement || st.ot_agreement || '',
        compAgreement: d.compAgreement || st.comp_agreement || '',
        restSplitAgreement: d.restSplitAgreement || st.rest_split_agreement || '',
        basePeriodMonths: d.basePeriodMonths || st.base_period_months || '1',
        laborManager: d.laborManager || st.labor_manager || '',
        dispatchOwner: d.dispatchOwner || st.dispatch_owner || '',
        shipowner: d.shipowner || st.shipowner || d.ownerName || '',
      }));
    }).catch(() => {});
  }, []);

  const applyVessel = (v?: Vessel) => {
    if (!v) return;
    setSelVessel({ id: v.id, name: v.vessel_name ?? '' });
    // 主機の種類、箇数及び出力（航行報告書用の結合表記）
    const engineCombined = [v.main_engine_type, v.main_engine_count ? `${v.main_engine_count}基` : '', v.main_engine_power ? `${v.main_engine_power}kW` : ''].filter(Boolean).join(' ');
    // 雇入届の主機の種類（汽/内燃）: マスタの文言から推定
    const hireEngineType = (v.main_engine_type ?? '').includes('汽') ? '汽' : (v.main_engine_type ? '内燃' : '');
    setData(d => ({
      ...d,
      shipName: v.vessel_name ?? '',
      ...(v.vessel_no ? { shipNo: v.vessel_no } : {}),
      ...(v.vessel_type ? { shipUse: v.vessel_type } : {}),
      ...(v.gross_tonnage ? { grossTonnage: v.gross_tonnage } : {}),
      ...(v.port_of_registry ? { portOfRegistry: v.port_of_registry } : {}),
      ...(engineCombined ? { mainEngine: engineCombined } : {}),
      ...(hireEngineType ? { mainEngineType: hireEngineType } : {}),
      ...(v.main_engine_power ? { mainEnginePower: v.main_engine_power } : {}),
      ...(v.owner_type && v.owner_type !== 'self'
        ? { ownerName: v.owner_name ?? d.ownerName, ownerAddress: [v.owner_address, v.owner_name].filter(Boolean).join('\n'), ownerAddrName: [v.owner_address, v.owner_name].filter(Boolean).join('\n') }
        : {}),
    }));
  };
  const applyCrew = (i: number, c: Crew) => setRows(rs => rs.map((r, idx) => idx === i ? {
    ...r, name: c.user_name, bookNo: c.seaman_book_no ?? r.bookNo, age: ageFromBirth(c.birth_date) || r.age, position: posMap[c.id] || c.license_grade || r.position,
  } : r));

  const [watchRows, setWatchRows] = useState<WatchRow[]>(() => Array.from({ length: 4 }, emptyWatch));
  const setWatchRow = (i: number, patch: Partial<WatchRow>) => setWatchRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const buildData = useCallback(() => ({
    ...data,
    rows: rows.map(r => ({ ...r, distinction: r.distinction || (form === 'koyoiri' ? '雇入' : '変更') })),
    ...(form === 'shipsupply' ? { supplyRows: supplyRows.filter(r => r.itemName.trim() || r.remarks.trim() || r.qty.trim()) } : {}),
    ...(form === 'travelexp' ? { travelRows: travelRows.filter(r => r.fromStation.trim() || r.toStation.trim() || r.transport.trim() || r.amount.trim()) } : {}),
    ...(form === 'koten' ? { watchRows: watchRows.filter(r => r.from.trim() || r.to.trim() || r.deck.trim() || r.engine.trim()) } : {}),
  }), [data, rows, supplyRows, travelRows, watchRows, form]);

  const refreshPreview = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/cert-forms/${form}?format=png`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ data: buildData() }) });
      if (!res.ok) return;
      const blob = await res.blob();
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch { /* noop */ }
  }, [form, buildData]);
  useEffect(() => { const t = setTimeout(refreshPreview, 700); return () => clearTimeout(t); }, [refreshPreview]);

  const download = async (fmt: 'pdf' | 'word' | 'excel') => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/cert-forms/${form}?format=${fmt}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ data: buildData() }) });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const ext = fmt === 'pdf' ? 'pdf' : fmt === 'word' ? 'docx' : 'xlsx';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = `${TITLES[form]}.${ext}`;
      if (fmt === 'pdf') a.target = '_blank';
      a.click(); URL.revokeObjectURL(a.href);
    } catch { alert('出力に失敗しました'); }
    setBusy(false);
  };

  // 印刷: PDFを生成して別タブ/ビューアで開き、端末の印刷サービス（Wi-Fi/USBプリンター・PDF保存）に渡す
  const printPdf = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/cert-forms/${form}?format=pdf`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ data: buildData() }) });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const w = window.open(url, '_blank');
      if (w) {
        // PDFビューアの読み込み後に印刷ダイアログを開く（失敗時はユーザーがビューアから印刷）
        try { w.addEventListener('load', () => { setTimeout(() => { try { w.focus(); w.print(); } catch { /* 手動印刷 */ } }, 600); }); } catch { /* noop */ }
      } else {
        // ポップアップブロック時はダウンロードにフォールバック
        const a = document.createElement('a'); a.href = url; a.download = `${TITLES[form]}.pdf`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert('印刷用PDFの生成に失敗しました'); }
    setBusy(false);
  };

  const setRow = (i: number, patch: Partial<Row>) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setSupplyRow = (i: number, patch: Partial<SupplyRow>) => setSupplyRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setTravelRow = (i: number, patch: Partial<TravelRow>) => setTravelRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const travelTotal = travelRows.reduce((a, r) => a + yenNum(r.amount), 0);
  const F = (label: string, key: string, opts: { textarea?: boolean; ph?: string } = {}) => (
    <div><label style={lb}>{label}</label>
      {opts.textarea
        ? <textarea style={ta} rows={2} value={data[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={opts.ph} />
        : <input style={inp} value={data[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={opts.ph} />}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', height: '100%' }}>
      {/* 左：入力 */}
      <div style={{ width: 430, flexShrink: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, maxHeight: '100%', overflowY: 'auto' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>{TITLES[form]}</div>

        {/* 船舶選択（全様式共通の自動補完） */}
        <label style={lb}>船舶を選択（船名・番号・所有者等を自動入力）</label>
        <select style={inp} onChange={e => applyVessel(vessels.find(x => x.id === e.target.value))}>
          <option value="">-- 船舶を選択 --</option>
          {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
        </select>

        {isHire(form) ? (
          <>
            {F('届出年月日', 'applicationDate', { ph: '令和 6 年 7 月 19 日' })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船舶番号', 'shipNo')}</div>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
              <div style={{ width: 90 }}>{F('総トン数', 'grossTonnage')}</div>
            </div>
            {F('届出者氏名（肩書つき・例: 船舶所有者 株式会社◯◯／船長 ◯◯）', 'reporterName')}
            {F('船舶所有者の氏名又は名称', 'ownerName')}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船舶の用途', 'shipUse')}</div>
              <div style={{ flex: 1 }}>{F('航行区域／従業区域', 'navArea')}</div>
            </div>
            {F('船舶所有者の住所及び氏名又は名称', 'ownerAddress', { textarea: true })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lb}>主機の種類</label>
                <select style={inp} value={data.mainEngineType ?? '内燃'} onChange={e => set('mainEngineType', e.target.value)}><option>内燃</option><option>汽</option></select>
              </div>
              <div style={{ flex: 1 }}>{F('主機の出力(kW)', 'mainEnginePower')}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>対象船員（{rows.length}名）</div>
              <button onClick={() => setRows(rs => [...rs, emptyRow()])} style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} />追加</button>
            </div>
            {rows.map((r, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>#{i + 1}</span>
                  {rows.length > 1 && <button onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                </div>
                <CrewPicker crew={crew} posMap={posMap} onPick={c => applyCrew(i, c)}
                  priorityVesselName={selVessel?.name} relatedIds={selVessel ? new Set(vesselUsers[selVessel.id] ?? []) : undefined} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 2 }}><label style={lb}>氏名</label><input style={inp} value={r.name} onChange={e => setRow(i, { name: e.target.value })} /></div>
                  <div style={{ flex: 2 }}><label style={lb}>船員手帳番号</label><input style={inp} value={r.bookNo} onChange={e => setRow(i, { bookNo: e.target.value })} /></div>
                  <div style={{ width: 52 }}><label style={lb}>年齢</label><input style={inp} value={r.age} onChange={e => setRow(i, { age: e.target.value })} /></div>
                  <div style={{ width: 74 }}><label style={lb}>区別</label>
                    <select style={inp} value={r.distinction || (form === 'koyoiri' ? '雇入' : '変更')} onChange={e => setRow(i, { distinction: e.target.value })}>
                      {(form === 'koyoiri' ? ['雇入', '雇止'] : ['変更', '更新']).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}><label style={lb}>雇入年月日</label><input style={inp} value={r.hireDate} onChange={e => setRow(i, { hireDate: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>雇入港</label><input style={inp} value={r.hirePort} onChange={e => setRow(i, { hirePort: e.target.value })} /></div>
                </div>
                <div><label style={lb}>職務</label><input style={inp} value={r.position} onChange={e => setRow(i, { position: e.target.value })} /></div>
                {form === 'koyoiri' ? (
                  <>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1 }}><label style={lb}>雇止年月日</label><input style={inp} value={r.endDate} onChange={e => setRow(i, { endDate: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={lb}>雇止港</label><input style={inp} value={r.endPort} onChange={e => setRow(i, { endPort: e.target.value })} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1 }}><label style={lb}>雇入期間</label><input style={inp} value={r.period} onChange={e => setRow(i, { period: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={lb}>給料</label><input style={inp} value={r.wage} onChange={e => setRow(i, { wage: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={lb}>手当</label><input style={inp} value={r.allowance} onChange={e => setRow(i, { allowance: e.target.value })} /></div>
                    </div>
                    <div><label style={lb}>その他の労働条件</label><input style={inp} value={r.otherConditions} onChange={e => setRow(i, { otherConditions: e.target.value })} /></div>
                    <div><label style={lb}>備考</label><input style={inp} value={r.remarks} onChange={e => setRow(i, { remarks: e.target.value })} /></div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1 }}><label style={lb}>変更(更新)年月日</label><input style={inp} value={r.changeDate} onChange={e => setRow(i, { changeDate: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={lb}>変更事項</label><input style={inp} value={r.changeItem} onChange={e => setRow(i, { changeItem: e.target.value })} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1 }}><label style={lb}>新</label><input style={inp} value={r.newValue} onChange={e => setRow(i, { newValue: e.target.value })} /></div>
                      <div style={{ flex: 1 }}><label style={lb}>旧</label><input style={inp} value={r.oldValue} onChange={e => setRow(i, { oldValue: e.target.value })} /></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        ) : form === 'koko' ? (
          <>
            {F('報告年月日', 'reportDate', { ph: '令和 6 年 7 月 19 日' })}
            {F('提出先（運輸局長）', 'bureauName', { ph: '四国運輸局長' })}
            {F('件名', 'subject', { ph: '例: 衝突 / 火災 / 遭難船舶救助' })}
            {F('船長氏名', 'masterName')}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
              <div style={{ width: 90 }}>{F('船質', 'hullMaterial', { ph: '鋼' })}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船舶番号', 'shipNo')}</div>
              <div style={{ flex: 1 }}>{F('船籍港', 'portOfRegistry')}</div>
              <div style={{ width: 90 }}>{F('総トン数', 'grossTonnage')}</div>
            </div>
            {F('航行区域又は従業制限及び従業区域', 'navArea')}
            {F('主機の種類、箇数及び出力', 'mainEngine', { ph: '例: ディーゼル 1基 1235kw' })}
            {F('船舶所有者の住所及び氏名又は名称', 'ownerAddrName', { textarea: true })}
            {F('船長の住所及び氏名', 'masterAddrName', { textarea: true })}
            {F('機関長の住所及び氏名', 'chiefEngAddrName', { textarea: true })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('海技免状番号（船長）', 'masterCertNo')}</div>
              <div style={{ flex: 1 }}>{F('海技免状番号（機関長）', 'chiefEngCertNo')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('発航港', 'departurePort')}</div>
              <div style={{ flex: 1 }}>{F('到達港', 'arrivalPort')}</div>
            </div>
            {F('事実発生の年月日時及び場所', 'eventDateTimePlace', { textarea: true })}
            {F('事実のてん末', 'eventDetail', { textarea: true })}
          </>
        ) : form === 'koten' ? (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('報告年月日', 'reportDate', { ph: '令和 8 年 11 月 2 日' })}</div>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
            </div>
            {F('避難日時（から）', 'evacFrom', { ph: '令和 8 年 11 月 1 日（土） 14 時 30 分' })}
            {F('避難日時（まで）', 'evacTo', { ph: '令和 8 年 11 月 2 日（日） 6 時 00 分' })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 2 }}>{F('避難場所', 'evacPlace')}</div>
              <div style={{ flex: 1 }}>{F('緯度', 'latitude')}</div>
              <div style={{ flex: 1 }}>{F('経度', 'longitude')}</div>
            </div>
            {F('避難理由', 'evacReason', { textarea: true })}
            <div style={{ display: 'flex', gap: 6 }}>
              {([['風向', 'windDir'], ['風速(m)', 'windSpeed'], ['天候', 'weatherCond'], ['視界(m)', 'visibility']] as const).map(([l, k]) => <div key={k} style={{ flex: 1 }}>{F(l, k)}</div>)}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {([['底質', 'seabed'], ['水深(m)', 'depth'], ['波高(m)', 'waveHeight'], ['備考', 'wxRemarks']] as const).map(([l, k]) => <div key={k} style={{ flex: 1 }}>{F(l, k)}</div>)}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {([['投錨 予定', 'anchorPlan'], ['投錨 実時間', 'anchorActual'], ['抜錨 予定', 'weighPlan'], ['抜錨 実時間', 'weighActual']] as const).map(([l, k]) => <div key={k} style={{ flex: 1 }}>{F(l, k, { ph: '14 時 30 分' })}</div>)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('使用錨鎖（節）', 'chainShackles')}</div>
              <div style={{ flex: 1 }}><label style={lb}>機関S/B</label>
                <select style={inp} value={data.engineStandby ?? ''} onChange={e => set('engineStandby', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select></div>
            </div>
            {F('略図（文章で。画像は船員アプリから）', 'sketchText', { textarea: true })}
            <label style={lb}>停泊当直時間（時間帯／甲板部／機関部）</label>
            {watchRows.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input style={{ ...inp, width: 70 }} value={r.from} placeholder="18:00" onChange={e => setWatchRow(i, { from: e.target.value })} />
                <span style={{ alignSelf: 'center', color: '#64748b' }}>～</span>
                <input style={{ ...inp, width: 70 }} value={r.to} placeholder="22:00" onChange={e => setWatchRow(i, { to: e.target.value })} />
                <input style={{ ...inp, flex: 1 }} value={r.deck} placeholder="甲板部" onChange={e => setWatchRow(i, { deck: e.target.value })} />
                <input style={{ ...inp, flex: 1 }} value={r.engine} placeholder="機関部" onChange={e => setWatchRow(i, { engine: e.target.value })} />
              </div>
            ))}
            {watchRows.length < 8 && <button onClick={() => setWatchRows(rs => [...rs, emptyWatch()])} style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>＋ 行を追加</button>}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('報告書作成者 役職', 'reporterTitle')}</div>
              <div style={{ flex: 1 }}>{F('報告書作成者 氏名', 'reporterName')}</div>
            </div>
          </>
        ) : form === 'tenmatsu' ? (
          <>
            {F('提出年月日', 'reportDate', { ph: '令和 8 年 11 月 2 日' })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('宛先（会社名）', 'companyName')}</div>
              <div style={{ flex: 1 }}>{F('代表取締役', 'companyRep')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
              <div style={{ flex: 1 }}>{F('船長名', 'masterName')}</div>
              <div style={{ flex: 1 }}>{F('機関長名', 'chiefEngName')}</div>
              <div style={{ flex: 1 }}>{F('港名', 'portName')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('当事者 役職', 'partyTitle')}</div>
              <div style={{ flex: 1 }}>{F('当事者 氏名', 'partyName')}</div>
              <div style={{ flex: 2 }}>{F('事例', 'caseTitle')}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {([['風向', 'windDir'], ['風速', 'windSpeed'], ['天候', 'weatherCond'], ['視界', 'visibility'], ['底質', 'seabed'], ['水深', 'depth'], ['波高', 'waveHeight'], ['備考', 'wxRemarks']] as const).map(([l, k]) => <div key={k} style={{ flex: 1 }}>{F(l, k)}</div>)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('発生日時', 'occurredAt', { ph: '11 月 1 日 14 時 30 分' })}</div>
              <div style={{ flex: 1 }}>{F('場所', 'occurredPlace')}</div>
              <div style={{ flex: 1 }}>{F('港名（荷役地名等）', 'occurredPort')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lb}>船内・船外</label>
                <select style={inp} value={data.inOutBoard ?? ''} onChange={e => set('inOutBoard', e.target.value)}><option value="">－</option><option>船内</option><option>船外</option></select></div>
              <div style={{ flex: 1 }}><label style={lb}>荷役中・荷役外</label>
                <select style={inp} value={data.cargoWork ?? ''} onChange={e => set('cargoWork', e.target.value)}><option value="">－</option><option>荷役中</option><option>荷役外</option></select></div>
              <div style={{ flex: 1 }}><label style={lb}>積荷</label>
                <select style={inp} value={data.cargoLoaded ?? ''} onChange={e => set('cargoLoaded', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select></div>
              <div style={{ flex: 1 }}>{F('製品名', 'productName')}</div>
            </div>
            {F('発生内容', 'incidentDetail', { textarea: true })}
            {F('発生原因', 'incidentCause', { textarea: true })}
            {F('対応', 'incidentResponse', { textarea: true })}
            {F('今後の対策', 'countermeasure', { textarea: true })}
            <div style={{ fontSize: 11.5, color: '#64748b' }}>※ 現場写真は船員アプリの顛末書から添付できます（PDFの2ページ目）。</div>
          </>
        ) : form === 'shipsupply' ? (
          <>
            <label style={lb}>宛先（○○ 御中）　※空欄なら自社名</label>
            <Combo value={data.supplierName ?? ''} onChange={v => set('supplierName', v)} options={opts.supplierName} ph={data.companyName || '例: 株式会社◯◯'} />
            {F('注文年月日', 'orderDate', { ph: '令和 8 年 11 月 2 日' })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
              <div style={{ width: 110 }}>
                <label style={lb}>部署</label>
                <select style={inp} value={data.department ?? '甲板部'} onChange={e => set('department', e.target.value)}>
                  {['甲板部', '機関部'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lb}>積込希望地</label>
                <Combo value={data.loadPort ?? ''} onChange={v => set('loadPort', v)} options={opts.loadPort} ph="今治" />
              </div>
              <div style={{ flex: 1 }}>{F('積込希望日', 'loadDate', { ph: '11 月 20 日' })}</div>
            </div>
            <label style={lb}>承認欄の氏名（船長／責任者／担当者）</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={inp} value={data.approverMaster ?? ''} placeholder="船長" onChange={e => set('approverMaster', e.target.value)} />
              <input style={inp} value={data.approverResp ?? ''} placeholder="責任者" onChange={e => set('approverResp', e.target.value)} />
              <input style={inp} value={data.approverStaff ?? ''} placeholder="担当者" onChange={e => set('approverStaff', e.target.value)} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>注文品（{supplyRows.length}件）</div>
              <button onClick={() => setSupplyRows(rs => [...rs, emptySupply()])} style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} />追加</button>
            </div>
            {supplyRows.map((r, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>#{i + 1}</span>
                  {supplyRows.length > 1 && <button onClick={() => setSupplyRows(rs => rs.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                </div>
                <label style={lb}>品名</label>
                {/* 品名を候補から選ぶと、マスタに登録された既定の寸法も一緒に入る */}
                <Combo value={r.itemName} onChange={v => setSupplyRow(i, { itemName: v })} options={opts.itemName} ph="例: 潤滑油 SAE40"
                  onPickExtra={v => setSupplyRow(i, { size: v })} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lb}>型式及び寸法</label>
                    <Combo value={r.size} onChange={v => setSupplyRow(i, { size: v })} options={opts.size} ph="20L缶" />
                  </div>
                  <div style={{ width: 70 }}><label style={lb}>在庫数</label><input style={inp} value={r.stock} onChange={e => setSupplyRow(i, { stock: e.target.value })} /></div>
                  <div style={{ width: 70 }}><label style={lb}>注文数</label><input style={inp} value={r.qty} onChange={e => setSupplyRow(i, { qty: e.target.value })} /></div>
                  <div style={{ width: 64 }}><label style={lb}>受取済</label>
                    <input type="checkbox" checked={r.received === '1'} onChange={e => setSupplyRow(i, { received: e.target.checked ? '1' : '' })} style={{ width: 22, height: 22, marginTop: 8 }} /></div>
                </div>
                <label style={lb}>備考欄</label>
                <Combo value={r.remarks} onChange={v => setSupplyRow(i, { remarks: v })} options={opts.remarks} />
              </div>
            ))}
          </>
        ) : form === 'travelexp' ? (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
              <div style={{ flex: 1 }}>{F('船長氏名', 'masterName')}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>旅費の明細（{travelRows.length}件）</div>
              <button onClick={() => setTravelRows(rs => [...rs, emptyTravel()])} style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} />追加</button>
            </div>
            {travelRows.map((r, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>#{i + 1}</span>
                  {travelRows.length > 1 && <button onClick={() => setTravelRows(rs => rs.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ width: 56 }}><label style={lb}>月</label><input style={inp} value={r.month} onChange={e => setTravelRow(i, { month: e.target.value })} /></div>
                  <div style={{ width: 56 }}><label style={lb}>日</label><input style={inp} value={r.day} onChange={e => setTravelRow(i, { day: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>金額（円）</label><input style={inp} value={r.amount} onChange={e => setTravelRow(i, { amount: e.target.value })} /></div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lb}>発駅</label>
                    <Combo value={r.fromStation} onChange={v => setTravelRow(i, { fromStation: v })} options={opts.station} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lb}>着駅</label>
                    <Combo value={r.toStation} onChange={v => setTravelRow(i, { toStation: v })} options={opts.station} />
                  </div>
                </div>
                <label style={lb}>交通内容（電車・タクシーなど）</label>
                <Combo value={r.transport} onChange={v => setTravelRow(i, { transport: v })} options={opts.transport} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', borderRadius: 8, background: '#0f172a', color: '#fff' }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>合計</span>
              <span style={{ fontSize: 16, fontWeight: 800 }}>{travelTotal.toLocaleString('ja-JP')} 円</span>
            </div>

            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 14, marginBottom: 4 }}>受領の報告</div>
            <CrewPicker crew={crew} posMap={posMap} placeholder="🔍 受領者を選択／検索して自動入力…"
              priorityVesselName={selVessel?.name} relatedIds={selVessel ? new Set(vesselUsers[selVessel.id] ?? []) : undefined}
              onPick={c => setData(d => ({ ...d, reportPerson: c.user_name, reportJobTitle: posMap[c.id] || c.license_grade || d.reportJobTitle || '' }))} />
            {F('報告年月日', 'receiptDate', { ph: '令和 7 年 9 月 13 日' })}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lb}>職名</label>
                <Combo value={data.reportJobTitle ?? ''} onChange={v => set('reportJobTitle', v)} options={opts.jobTitle} ph="一等航海士" />
              </div>
              <div style={{ flex: 1 }}>{F('氏名', 'reportPerson')}</div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.8, marginTop: 6 }}>
              ※本人捺印及び船長もしくは担当者の確認印がないものは無効とする<br />
              ※旅費以外の精算書は記入しないようお願いいたします
            </div>
          </>
        ) : form === 'roumu' ? (
          <>
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 10px', fontSize: 11.5, color: '#0c4a6e', lineHeight: 1.7, marginBottom: 4 }}>
              国土交通省「船員の労働時間管理支援ツール」と同じ体裁の記録簿を、CATMSの勤怠から作ります。
              作業の開始・終了、休息時間、休日・有給は打刻と休暇申請から自動で入ります。
            </div>
            <label style={lb}>対象の船員</label>
            <CrewPicker crew={crew} posMap={posMap} placeholder="🔍 船員を選択／検索…"
              priorityVesselName={selVessel?.name} relatedIds={selVessel ? new Set(vesselUsers[selVessel.id] ?? []) : undefined}
              onPick={c => setData(d => ({ ...d, userId: c.id, userName: c.user_name }))} />
            <input style={{ ...inp, background: '#f8fafc' }} readOnly value={data.userName ?? ''} placeholder="未選択" />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lb}>年</label>
                <select style={inp} value={data.year ?? ''} onChange={e => set('year', e.target.value)}>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lb}>月</label>
                <select style={inp} value={data.month ?? ''} onChange={e => set('month', e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 14, marginBottom: 4 }}>様式の設定</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lb}>時間外労働協定</label>
                <select style={inp} value={data.overtimeAgreement ?? ''} onChange={e => set('overtimeAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
              </div>
              <div style={{ flex: 1 }}><label style={lb}>補償休日労働協定</label>
                <select style={inp} value={data.compAgreement ?? ''} onChange={e => set('compAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
              </div>
              <div style={{ flex: 1 }}><label style={lb}>休息時間分割協定</label>
                <select style={inp} value={data.restSplitAgreement ?? ''} onChange={e => set('restSplitAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>{F('基準労働期間（月）', 'basePeriodMonths', { ph: '1' })}</div>
              <div style={{ flex: 1.4 }}>{F('起算日', 'periodFrom', { ph: '2026-04-01' })}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label style={lb}>休日日数の定め</label>
                <select style={inp} value={data.holidayDefined ?? '有'} onChange={e => set('holidayDefined', e.target.value)}><option>有</option><option>無</option></select>
              </div>
              <div style={{ flex: 1 }}>{F('休日日数', 'holidayDays', { ph: '4' })}</div>
              <div style={{ flex: 1 }}>{F('有給の必要日数', 'paidLeaveDays', { ph: '10' })}</div>
            </div>
            {F('労務管理責任者', 'laborManager')}
            {F('船舶所有者', 'shipowner')}
            {F('派遣元船舶所有者（派遣の場合）', 'dispatchOwner')}
          </>
        ) : (
          <>
            {F('申請年月日', 'applicationDate', { ph: '令和 6 年 7 月 19 日' })}
            {F('提出先（運輸局長）', 'bureauName', { ph: '四国運輸局長' })}
            {F('申請者氏名（「船長」又は「船舶所有者」の肩書を付す）', 'applicantName', { ph: '例: 船舶所有者 国土交通株式会社' })}
            {F('住所', 'applicantAddress', { textarea: true })}
            {F('報告先', 'reportAddressee')}
            {F('報告年月日', 'reportDate', { ph: '令和 6 年 7 月 18 日' })}
            {F('証明書の通数', 'certCount')}
            {F('証明書の用途', 'certPurpose', { textarea: true })}
          </>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 12, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 8 }}>
          <button disabled={busy} onClick={printPdf} title="PDFを開いて印刷（プリンター／PDF保存）" style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', background: 'linear-gradient(135deg,#334155,#0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Printer size={14} />印刷</button>
          <button disabled={busy} onClick={() => download('pdf')} style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', background: 'linear-gradient(135deg,#0ea5e9,#0284c7)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Download size={14} />PDF</button>
          {/* 見本と同じ形式のみ提供：雇入系・社内様式=Excel、航行報告系=Word、荒天避難・顛末書=PDFのみ */}
          {isPdfOnly(form) ? null : isHire(form) || isInternal(form) || isFromAttendance(form)
            ? <button disabled={busy} onClick={() => download('excel')} style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>Excel</button>
            : <button disabled={busy} onClick={() => download('word')} style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' }}>Word</button>}
        </div>
      </div>

      {/* 右：プレビュー */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>プレビュー</span>
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
            <button onClick={() => setZoom(z => Math.max(0.6, +(z - 0.2).toFixed(2)))} style={{ width: 30, height: 28, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>－</button>
            <span style={{ minWidth: 48, textAlign: 'center', fontSize: 12, lineHeight: '28px', color: '#475569' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(2)))} style={{ width: 30, height: 28, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>＋</button>
            <button onClick={() => setZoom(1)} style={{ padding: '0 10px', height: 28, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 12 }}>全体</button>
            <button onClick={() => localStorage.setItem('cert_preview_zoom', String(zoom))} title="この拡大率を既定にする" style={{ padding: '0 10px', height: 28, borderRadius: 6, border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>既定に設定</button>
          </div>
        </div>
        <div style={{ flex: 1, background: '#64748b', borderRadius: 12, padding: 12, overflow: 'auto' }}>
          {previewUrl
            ? <img alt="プレビュー" src={previewUrl} style={{ width: `${zoom * 100}%`, maxWidth: 'none', display: 'block', margin: '0 auto', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.35)' }} />
            : <div style={{ color: '#e2e8f0', padding: 40, fontSize: 13, textAlign: 'center' }}>入力するとプレビューが表示されます…</div>}
        </div>
      </div>
    </div>
  );
};

export default CertFormsPanel;
